import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import CaiImplementationBrief from "./CaiImplementationBrief";
import { inspectCaiOutputSafety } from "../../lib/currentAssetIntelligence/caiCustomerOutputSafety";
import {
  buildCaiOpportunityIdempotencyKey,
} from "../../lib/currentAssetIntelligence/caiOpportunityRunModel";
import {
  getCaiOpportunityJobStatus,
  submitCaiOpportunityJob,
  resumeExistingCaiOpportunityJob,
  type CaiDurableJobSubmission,
} from "../../lib/caiOpportunityRunApi";

type DurableStatus = Record<string, any>;
type DurableStatusResponse = DurableStatus | null | undefined;

export type CaiDurableJobApi = {
  submit: (input: CaiDurableJobSubmission) => Promise<DurableStatus>;
  getStatus: (input: { accountId: string; jobId?: string; idempotencyKey?: string; runId?: string; recommendationSourceKey?: string }) => Promise<DurableStatus>;
  resumeExisting: (input: { resumeExistingJobId: string; accountId: string; runId: string; recommendationId: string; recommendationSourceKey: string; productArea: "local" | "competitive" }) => Promise<DurableStatus>;
};

export type CaiDurableJobPanelProps = {
  briefLayout?: "persistent" | "focus";
  onSavedBriefChange?: (ready: boolean) => void;
  onStatusChange?: (status: string) => void;
  workspacePresentation?: boolean;
  onFocusChange?: (focused: boolean) => void;
  onReadingFocusChange?: (focused: boolean) => void;
  generationEnabled?: boolean;
  isPlatformAdmin?: boolean;
  enabled?: boolean;
  accountName: string;
  api?: Partial<CaiDurableJobApi>;
  initialResponse?: Record<string, any> | null;
  opportunity: CaiDurableJobSubmission & {
    opportunityCategory?: "asset" | "entity" | "competitive" | null;
  };
};

const ACTIVE_STATUSES = new Set(["pending", "running"]);
const TERMINAL_STATUSES = new Set(["completed", "needs_review", "failed", "quota_exhausted"]);
const STAGE_COPY: Record<string, { label: string; message: string }> = {
  opportunity_verification: { label: "Reviewing the opportunity", message: "Toren is verifying the selected work." },
  evidence_assembly: { label: "Preparing evidence", message: "Toren is assembling relevant observations and supporting context." },
  provider_generation: { label: "Toren is preparing your brief", message: "Generation is in progress." },
  safety_inspection: { label: "Reviewing the recommendation", message: "Safety and quality validation are underway." },
  quality_validation: { label: "Reviewing the recommendation", message: "Safety and quality validation are underway." },
  persistence: { label: "Saving your brief", message: "Your optimization brief is being saved." },
};

function statusCode(response: DurableStatus | null | undefined): string {
  return typeof response?.code === "string" ? response.code : "";
}

function isStatusResponse(value: DurableStatusResponse): value is DurableStatus {
  return Boolean(value && typeof value === "object" && !Array.isArray(value)
    && (typeof value.status === "string" || typeof value.code === "string"));
}

function isMissingJob(response: DurableStatus | null | undefined): boolean {
  return response?.status === "not_found" || statusCode(response) === "cai_job_not_found";
}

function isStatusUnavailable(response: DurableStatus | null | undefined): boolean {
  return response?.status === "status_unavailable"
    || ["cai_auth_required", "cai_status_unavailable", "cai_status_identity_missing"].includes(statusCode(response));
}

function customerFailureMessage(response: DurableStatus | null | undefined): string {
  const code = statusCode(response);
  if (code === "cai_auth_required") return "Your session is no longer available. Sign in again to check this brief.";
  if (code === "cai_durable_config_unavailable" || code === "cai_durable_enqueue_unavailable") return "CAI is temporarily unavailable. Try again when the service is ready.";
  if (code === "provider_timeout" || code === "cai_computation_timeout") return "Generation took too long to finish. Review the opportunity before trying again.";
  if (code === "unsafe_output_rejected") return "Toren stopped this brief because the draft could not be made customer-safe.";
  if (code === "provider_outcome_uncertain") return "Toren needs to review the generation result before this brief can continue.";
  if (code === "quota_exhausted" || response?.status === "quota_exhausted") return "Monthly CAI limit reached. This brief was not started.";
  return response?.status === "needs_review"
    ? "This brief needs review before Toren can provide a customer-safe result."
    : "Toren could not complete this brief. Review the selected opportunity and try again.";
}

function stageFor(response: DurableStatus | null | undefined) {
  if (!response) return { label: "Queued", message: "Your optimization brief is waiting to begin." };
  if (response.status === "pending") return { label: "Queued", message: "Your optimization brief is waiting to begin." };
  return STAGE_COPY[String(response.currentStage || response.job?.executionStage || "")] || {
    label: response.status === "running" ? "In progress" : "Processing",
    message: "Toren is processing the selected opportunity.",
  };
}

function headerLabel(status: string, stage: { label: string }): string {
  if (status === "preview_read_only") return "Preview read-only";
  if (status === "completed") return "Brief ready";
  if (status === "needs_review") return "Needs review";
  if (status === "failed") return "Brief not completed";
  if (status === "quota_exhausted") return "Quota exhausted";
  if (status === "status_unavailable") return "Status unavailable";
  if (status === "pending") return "Queued";
  if (status === "running") return stage.label;
  return status === "ready" ? "Ready" : "Needs attention";
}


export default function CaiDurableJobPanel({ enabled = false, generationEnabled = false, isPlatformAdmin = false, accountName, api, initialResponse = null, opportunity, workspacePresentation = false, briefLayout = "persistent", onFocusChange, onReadingFocusChange, onSavedBriefChange, onStatusChange }: CaiDurableJobPanelProps) {
  const categoryEnabled = opportunity.opportunityCategory === "asset"
    && Boolean(opportunity.recommendationSourceKey?.startsWith("asset:"));
  const canGenerate = generationEnabled && categoryEnabled;
  const jobApi = useMemo<CaiDurableJobApi>(() => ({
    submit: api?.submit || submitCaiOpportunityJob,
    getStatus: api?.getStatus || getCaiOpportunityJobStatus,
    resumeExisting: api?.resumeExisting || resumeExistingCaiOpportunityJob,
  }), [api]);
  const identity = useMemo(() => ({
    accountId: opportunity.accountId,
    runId: opportunity.runId || null,
    recommendationId: opportunity.recommendationId || null,
    recommendationKey: opportunity.recommendationSourceKey || opportunity.recommendationKey || null,
    recommendationSourceKey: opportunity.recommendationSourceKey || opportunity.recommendationKey || null,
    productArea: opportunity.productArea,
    outputType: "CAI Output",
    recommendationType: opportunity.recommendationType || null,
    recommendationTitle: opportunity.recommendationTitle || null,
    entityArea: opportunity.entityArea || null,
    opportunityCategory: opportunity.opportunityCategory || null,
    assetFocused: opportunity.assetFocused === true,
  }), [opportunity]);
  const idempotencyKey = useMemo(() => buildCaiOpportunityIdempotencyKey(identity), [identity]);
  const [focused, setFocused] = useState(false);
  const [readingFocus, setReadingFocus] = useState(false);
  const briefHeading = useRef<HTMLDivElement>(null);
  const openBriefButton = useRef<HTMLButtonElement>(null);
  const wasFocused = useRef(false);
  useEffect(() => {
    const initialFocus = window.history.state?.caiWorkspace?.identity === idempotencyKey;
    setFocused(initialFocus);
    setReadingFocus(initialFocus && window.history.state?.caiWorkspace?.mode === "focus");
    onFocusChange?.(initialFocus);
    const pop = () => {
      const next = window.history.state?.caiWorkspace?.identity === idempotencyKey;
      setFocused(next);
      setReadingFocus(next && window.history.state?.caiWorkspace?.mode === "focus");
      onFocusChange?.(next);
    };
    window.addEventListener("popstate", pop);
    return () => window.removeEventListener("popstate", pop);
  }, [idempotencyKey, onFocusChange]);
  const focusView = focused && (readingFocus || briefLayout === "focus");
  useEffect(() => { onReadingFocusChange?.(focusView); }, [focusView, onReadingFocusChange]);
  useEffect(() => {
    if (focused) briefHeading.current?.focus();
    else if (wasFocused.current) openBriefButton.current?.focus();
    wasFocused.current = focused;
  }, [focused, focusView]);
  const openBrief = () => {
    if (window.history.state?.caiWorkspace?.identity !== idempotencyKey) window.history.pushState({ ...window.history.state, caiWorkspace: { identity: idempotencyKey, mode: briefLayout } }, "", window.location.href);
    setReadingFocus(briefLayout === "focus");
    setFocused(true);
    onFocusChange?.(true);
  };
  const [response, setResponse] = useState<DurableStatus | null>(() => initialResponse);
  useEffect(() => {
    const ready = response?.status === "completed" && response?.caiOutput && response?.outputWithheld !== true && inspectCaiOutputSafety(response.caiOutput).status === "passed";
    onSavedBriefChange?.(Boolean(ready));
    onStatusChange?.(response?.status === "completed" && !ready ? "output_unavailable" : String(response?.status || "unknown"));
  }, [response, onSavedBriefChange, onStatusChange]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [statusUnavailable, setStatusUnavailable] = useState(false);
  const [statusNotice, setStatusNotice] = useState("");
  const [recoveryNotice, setRecoveryNotice] = useState("");
  const [recoverySubmitting, setRecoverySubmitting] = useState(false);
  const [recoverySubmitted, setRecoverySubmitted] = useState(false);
  const [verifiedIdentity, setVerifiedIdentity] = useState<string | null>(null);
  const requestInFlight = useRef(false);
  const generationRef = useRef(0);
  const submissionInFlight = useRef(false);
  const recoveryInFlight = useRef(false);
  const recoveryAttempted = useRef(false);
  const mountedRef = useRef(false);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const statusInput = useMemo(() => ({
    accountId: opportunity.accountId,
    idempotencyKey,
    runId: opportunity.runId || undefined,
    recommendationSourceKey: opportunity.recommendationSourceKey || opportunity.recommendationKey || undefined,
  }), [opportunity.accountId, opportunity.runId, opportunity.recommendationKey, opportunity.recommendationSourceKey, idempotencyKey]);

  const applyStatus = useCallback((next: DurableStatusResponse, generation: number) => {
    if (!mountedRef.current || generation !== generationRef.current) return;
    if (!isStatusResponse(next)) {
      setStatusUnavailable(true);
      setStatusNotice("Toren cannot retrieve the current status right now. Status is temporarily unavailable; this does not establish that the underlying job failed.");
      return;
    }
    if (isStatusUnavailable(next)) {
      setStatusUnavailable(true);
      setStatusNotice(statusCode(next) === "cai_auth_required" ? "Your session is no longer available. Sign in again to check this brief." : "Toren cannot retrieve the current status right now. Status is temporarily unavailable; this does not establish that the underlying job failed.");
      return;
    }
    setStatusUnavailable(false);
    setVerifiedIdentity(idempotencyKey);
    setStatusNotice("");
    if (isMissingJob(next)) {
      setResponse(null);
      return;
    }
    if (next.status || next.code) setResponse(next);
  }, [idempotencyKey]);

  const refreshStatus = useCallback(async (generation = generationRef.current) => {
    if (requestInFlight.current) return null;
    requestInFlight.current = true;
    if (mountedRef.current) setLoading(true);
    try {
      const next = await jobApi.getStatus(statusInput);
      applyStatus(next, generation);
      return next;
    } catch {
      const unavailable = { status: "status_unavailable", code: "cai_status_unavailable" };
      applyStatus(unavailable, generation);
      return unavailable;
    } finally {
      requestInFlight.current = false;
      if (mountedRef.current && generation === generationRef.current) setLoading(false);
    }
  }, [applyStatus, jobApi, statusInput]);

  useEffect(() => {
    if (!enabled) return undefined;
    const generation = generationRef.current + 1;
    generationRef.current = generation;
    setResponse(initialResponse);
    setStatusUnavailable(false);
    setStatusNotice("");
    setVerifiedIdentity(null);
    void refreshStatus(generation);
    return undefined;
  }, [enabled, idempotencyKey, initialResponse, refreshStatus]);

  const responseStatus = response?.status;

  const recoveryJob = response?.job;
  useEffect(() => {
    recoveryAttempted.current = false;
    setRecoverySubmitted(false);
    setRecoveryNotice("");
  }, [recoveryJob?.id]);
  const recoveryEligible = Boolean(isPlatformAdmin
    && generationEnabled
    && categoryEnabled
    && response?.status === "pending"
    && response?.existingJobRecoveryEligible === true
    && recoveryJob?.id
    && recoveryJob.status === "pending"
    && recoveryJob.dispatchStatus === "queued"
    && recoveryJob.dispatchAttemptCount === 0
    && recoveryJob.attemptCount === 0
    && recoveryJob.providerInvocationState === "not_started"
    && recoveryJob.usageCounted === false
    && recoveryJob.accountId === opportunity.accountId
    && recoveryJob.runId === opportunity.runId
    && recoveryJob.recommendationId === opportunity.recommendationId
    && recoveryJob.recommendationSourceKey === opportunity.recommendationSourceKey
    && recoveryJob.productArea === opportunity.productArea
    && recoveryJob.idempotencyKey === idempotencyKey
    && recoveryJob.outputType === opportunity.recommendedOutput);

  const resumeQueuedJob = useCallback(async () => {
    if (!recoveryEligible || recoveryInFlight.current || recoveryAttempted.current) return;
    const confirmed = window.confirm(
      `Resume existing CAI job ${recoveryJob.id} for ${accountName}? This can start AI generation and consume quota. No new job will be created. Continue?`,
    );
    if (!confirmed) return;
    recoveryAttempted.current = true;
    setRecoverySubmitted(true);
    recoveryInFlight.current = true;
    setRecoverySubmitting(true);
    setRecoveryNotice("");
    try {
      const result = await jobApi.resumeExisting({
        resumeExistingJobId: recoveryJob.id,
        accountId: opportunity.accountId,
        runId: opportunity.runId || "",
        recommendationId: opportunity.recommendationId || "",
        recommendationSourceKey: opportunity.recommendationSourceKey,
        productArea: opportunity.productArea,
      });
      if (result.recovery === "existing_job_only" && result.job?.id === recoveryJob.id && result.dispatch?.attempted === true) {
        setRecoveryNotice("The existing job recovery request was accepted. Checking its saved status…");
      } else if (result.status === "uncertain" || result.code === "cai_recovery_outcome_uncertain") {
        setRecoveryNotice("The request outcome is uncertain. Do not submit it again; check the saved job status before deciding what to do next.");
      } else {
        setRecoveryNotice("The recovery request was not accepted. No automatic retry was made; review the saved job status.");
      }
      await refreshStatus();
    } catch {
      setRecoveryNotice("The request outcome is uncertain. Do not submit it again; check the saved job status before deciding what to do next.");
      await refreshStatus();
    } finally {
      recoveryInFlight.current = false;
      if (mountedRef.current) setRecoverySubmitting(false);
    }
  }, [accountName, jobApi, opportunity, recoveryEligible, recoveryJob, refreshStatus]);

  useEffect(() => {
    if (!enabled || !responseStatus || !ACTIVE_STATUSES.has(String(responseStatus))) return undefined;
    let cancelled = false;
    let timer: ReturnType<typeof setTimeout> | undefined;
    let attempt = 0;
    const poll = async () => {
      if (cancelled) return;
      const next = await refreshStatus(generationRef.current);
      if (cancelled) return;
      attempt += 1;
      const delay = isStatusUnavailable(next) ? Math.min(12000, 3000 * attempt) : Math.min(8000, 1500 + attempt * 500);
      timer = setTimeout(poll, delay);
    };
    timer = setTimeout(poll, 1200);
    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
  }, [enabled, responseStatus, refreshStatus]);

  const submit = useCallback(async (retryRequested = false) => {
    const existingStatus = String(response?.status || "ready");
    const canRetry = TERMINAL_STATUSES.has(existingStatus) && existingStatus !== "completed"
      && (response?.retryEligible === true || response?.job?.retryEligible === true);
    if (!canGenerate || submissionInFlight.current || requestInFlight.current || verifiedIdentity !== idempotencyKey
      || (retryRequested ? !canRetry : !["ready", "not_found"].includes(existingStatus))) return;
    submissionInFlight.current = true;
    if (mountedRef.current) {
      setSubmitting(true);
      setStatusNotice("");
    }
    try {
      const result = await jobApi.submit({ ...opportunity, idempotencyKey, retryRequested });
      applyStatus(result, generationRef.current);
      if (mountedRef.current && (isStatusUnavailable(result) || result.code === "cai_auth_required" || result.status === "needs_configuration")) {
        setStatusNotice(customerFailureMessage(result));
      }
    } finally {
      submissionInFlight.current = false;
      if (mountedRef.current) setSubmitting(false);
    }
  }, [applyStatus, canGenerate, idempotencyKey, jobApi, opportunity, response, verifiedIdentity]);

  if (!enabled) return null;
  const stage = stageFor(response);
  const rawStatus = String(response?.status || "ready");
  const status = statusUnavailable ? "status_unavailable" : rawStatus;
  const retryEligible = response?.retryEligible === true || response?.job?.retryEligible === true;
  const candidateOutput = response?.caiOutput && typeof response.caiOutput === "object" ? response.caiOutput : null;
  const outputWithheld = response?.outputWithheld === true || Boolean(candidateOutput && inspectCaiOutputSafety(candidateOutput).status !== "passed");
  const output = outputWithheld ? null : candidateOutput;
  const isTerminalFailure = TERMINAL_STATUSES.has(status) && status !== "completed";
  const dispatchTemporarilyUnavailable = status === "pending" && ["failed", "unknown"].includes(String(response?.job?.dispatchStatus || response?.dispatch?.status || ""));
  return <section className="toren-surface" data-testid="cai-durable-job-panel" data-cai-status={status} data-cai-stage={response?.currentStage || response?.job?.executionStage || "queued"} style={{ marginTop: 12, minWidth: 0, maxWidth: "100%", overflowWrap: "anywhere" }}>
    {!(workspacePresentation && status === "completed" && output) && <div style={{ display: "flex", flexWrap: "wrap", justifyContent: "space-between", gap: 10, alignItems: "flex-start" }}>
      <div style={{ minWidth: 0, flex: "1 1 220px" }}>
        <div style={{ color: "#1d4ed8", fontSize: 11, fontWeight: 900, textTransform: "uppercase", letterSpacing: "0.04em" }}>CAI optimization brief</div>
        <h3 style={{ margin: "5px 0 0", color: "#172554", fontSize: 16, minWidth: 0, overflowWrap: "anywhere" }}>{opportunity.recommendationTitle || "Selected opportunity"}</h3>
        <div style={{ marginTop: 3, color: "#64748b", fontSize: 12 }}>{accountName}</div>
      </div>
      <div data-testid="cai-durable-status" style={{ color: status === "completed" ? "#166534" : "#1e3a8a", fontSize: 12, fontWeight: 900, flex: "0 1 auto" }}>{status === "ready" && !canGenerate ? "Evidence available" : headerLabel(status, stage)}</div>
    </div>}
    {focused && output && <div className="cai-brief-wayfinding">
      <div ref={briefHeading} tabIndex={-1} role="navigation" aria-label="Brief location">Opportunity Engine <span aria-hidden="true"> / </span>{opportunity.opportunityCategory === "asset" ? "Assets" : opportunity.opportunityCategory === "entity" ? "Entity" : "Competitive"}<span aria-hidden="true"> / </span>{opportunity.recommendationTitle}<span aria-hidden="true"> / </span>Implementation Brief</div>
      <button className="cai-brief-back" type="button" onClick={() => window.history.back()}>{focusView ? "Back to Opportunities" : "Back to Overview"}</button>
    </div>}
    {!generationEnabled && status !== "completed" ? <p data-testid="cai-generation-disabled">Brief generation is not enabled for this account. Saved briefs remain available.</p> : null}
    {!categoryEnabled ? <p data-testid="cai-category-generation-disabled">New optimization briefs are currently available from Assets only. You can still review this opportunity and read saved briefs.</p> : null}
    {canGenerate && status === "ready" ? <p>Generating an optimization brief does not publish or modify your website.</p> : null}
    {canGenerate && status === "ready" && !statusUnavailable ? <>
      <p data-testid="cai-durable-next-action" style={{ margin: "10px 0 0", color: "#475569", lineHeight: 1.45 }}>{opportunity.nextStep ? `Next action: ${opportunity.nextStep}. ` : ""}Start a brief for this selected opportunity. You can navigate elsewhere and return to see the same progress or result.</p>
      <button type="button" data-testid="cai-durable-start" onClick={() => void submit(false)} disabled={submitting || loading || verifiedIdentity !== idempotencyKey} className="toren-control toren-control--primary" style={{ marginTop: 10 }}>{submitting ? "Starting…" : "Generate optimization brief"}</button>
    </> : null}
    {ACTIVE_STATUSES.has(status) ? <>
      <p data-testid="cai-durable-progress-message" style={{ margin: "10px 0 0", color: "#334155", lineHeight: 1.45 }}>{stage.message}</p>
      <p data-testid="cai-durable-return-copy" style={{ margin: "7px 0 0", color: "#64748b", fontSize: 12, lineHeight: 1.4 }}>You can navigate elsewhere and return to see the same progress.</p>
      {dispatchTemporarilyUnavailable ? <p data-testid="cai-durable-recovery" style={{ margin: "7px 0 0", color: "#92400e", lineHeight: 1.4 }}>The brief is saved and waiting for dispatch. Toren will check again; this is not a generation failure.</p> : null}
      {loading ? <div style={{ marginTop: 7, color: "#64748b", fontSize: 11 }}>Checking saved status…</div> : null}
    </> : null}
    {recoveryEligible && !recoverySubmitted ? <div data-testid="cai-existing-job-recovery" style={{ marginTop: 12, padding: 10, border: "1px solid #f59e0b", borderRadius: 8, background: "#fffbeb" }}>
      <strong>Internal operator action</strong>
      <p style={{ margin: "5px 0 8px", color: "#78350f", lineHeight: 1.4 }}>This saved job is still queued. A platform administrator may resume this exact job; the server rechecks its account, reviewed asset, entitlement, identity, and claimability.</p>
      <button type="button" data-testid="cai-existing-job-resume" className="toren-control toren-control--secondary" disabled={recoverySubmitting || loading} onClick={() => void resumeQueuedJob()}>{recoverySubmitting ? "Submitting once…" : "Resume existing queued job"}</button>
    </div> : null}
    {recoverySubmitted && recoveryNotice ? <p data-testid="cai-existing-job-recovery-notice" role="status" style={{ marginTop: 10, color: "#92400e", lineHeight: 1.4 }}>{recoveryNotice}</p> : null}
    {status === "preview_read_only" ? <p data-testid="cai-durable-preview-read-only" style={{ marginTop: 10, color: "#475569", lineHeight: 1.4 }}>Brief generation is disabled for customer accounts in Preview.</p> : null}
    {statusUnavailable ? <div data-testid="cai-durable-status-unavailable" style={{ marginTop: 10, padding: 9, borderRadius: 8, background: "#fff7ed", color: "#9a3412", lineHeight: 1.4 }}>{statusNotice}</div> : null}
    {statusUnavailable ? <button type="button" data-testid="cai-durable-refresh-status" onClick={() => void refreshStatus(generationRef.current)} disabled={loading} className="toren-control toren-control--secondary" style={{ marginTop: 8 }}>{loading ? "Checking…" : "Check saved status"}</button> : null}
    {status === "completed" && output && workspacePresentation && !focused ? <div className="oe-detail-context">
      <div className="cai-brief-meta">CAI optimization brief · Brief ready</div>
      <h3>{output.outputTitle || output.title || "Saved implementation brief"}</h3>
      <p className="cai-opportunity-origin">Opportunity: {opportunity.recommendationTitle}</p>
      {opportunity.targetAsset && <p><strong>Target asset:</strong> {opportunity.targetAsset}</p>}
      {typeof output.implementationSummary === "string" && <p>{output.implementationSummary}</p>}
      {typeof output.evidenceSummary === "string" && <details><summary>Supporting rationale</summary><p>{output.evidenceSummary}</p></details>}
      <button ref={openBriefButton} className="cai-brief-back toren-control toren-control--primary" type="button" onClick={openBrief}>Open Implementation Brief</button>
    </div> : null}
    {status === "completed" && output && (!workspacePresentation || focused) ? <CaiImplementationBrief output={output} assetFocused={opportunity.assetFocused === true || (opportunity.opportunityCategory === "asset" && opportunity.recommendationType === "existing_page_optimization")} selectedTargetUrl={opportunity.targetAsset || ""} onFocusBrief={workspacePresentation && !focusView ? () => { window.history.pushState({ ...window.history.state, caiWorkspace: { identity: idempotencyKey, mode: "focus" } }, "", window.location.href); setReadingFocus(true); } : undefined} /> : null}
    {status === "completed" && !output ? <div data-testid="cai-durable-output-unavailable" style={{ marginTop: 10, color: "#9a3412" }}>{outputWithheld ? "This saved brief requires review before it can be displayed." : "The brief is marked complete, but its saved output is temporarily unavailable. Check saved status again."}</div> : null}
    {isTerminalFailure ? <div data-testid="cai-durable-failure" style={{ marginTop: 10, padding: 10, borderRadius: 8, background: "#fff7ed", color: "#9a3412", lineHeight: 1.45 }}>
      <strong>{status === "needs_review" ? "Needs review" : "Brief not completed"}</strong>
      <div>{customerFailureMessage(response)}</div>
      {retryEligible && canGenerate ? <button type="button" data-testid="cai-durable-retry" onClick={() => void submit(true)} disabled={submitting} className="toren-control toren-control--secondary" style={{ marginTop: 9 }}>{submitting ? "Retrying…" : "Retry this brief"}</button> : null}
    </div> : null}
    {statusNotice && !statusUnavailable && !isTerminalFailure ? <div data-testid="cai-durable-notice" style={{ marginTop: 9, color: "#9a3412", lineHeight: 1.4 }}>{statusNotice}</div> : null}
    {status !== "completed" && status !== "ready" ? <div data-testid="cai-durable-selection-identity" style={{ marginTop: 9, color: "#64748b", fontSize: 10 }}>This status belongs to the selected opportunity and account.</div> : null}
  </section>;
}
